<div class="app-main__inner">

    <div class="app-page-title">
        <div class="page-title-wrapper">
            <div class="page-title-heading">
                <div class="page-title-icon">
                    <i class="pe-7s-ribbon icon-gradient bg-tempting-azure"></i>
                </div>
                <div>
                    Template Creation
                    <div class="page-title-subheading">
                        Build and submit WhatsApp carousel templates for Meta approval.
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Toolbar -->
    <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="d-flex align-items-center gap-2">
            <span *ngIf="templateResult" class="alert py-2 px-3 mb-0"
                [ngClass]="templateResultSuccess ? 'alert-success' : 'alert-danger'">
                <i [ngClass]="templateResultSuccess ? 'pe-7s-check' : 'pe-7s-attention'" class="me-1"></i>
                {{ templateResult }}
            </span>
        </div>
        <div class="d-flex gap-2">
            <button class="btn btn-outline-warning btn-sm d-flex align-items-center gap-1"
                (click)="showRejectionTips = !showRejectionTips"
                [class.active]="showRejectionTips"
                title="Why do templates get rejected?">
                <i class="pe-7s-info" style="font-size:1rem;"></i>
                Why templates get rejected
            </button>
            <button class="btn btn-primary" (click)="toggleBuilderForm()">
                <i class="pe-7s-plus me-1"></i>
                {{ showBuilderForm ? 'Cancel' : 'Create Template' }}
            </button>
        </div>
    </div>

    <!-- Rejection Tips Panel -->
    <div *ngIf="showRejectionTips" class="rejection-tips-panel mb-4">
        <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="mb-0 fw-semibold" style="color:#b45309;">
                <i class="pe-7s-attention me-1"></i> Common Reasons Meta Rejects Templates
            </h6>
            <button class="btn btn-sm btn-outline-secondary" (click)="showRejectionTips = false">✕ Close</button>
        </div>
        <div class="tips-grid">

            <div class="tip-card">
                <div class="tip-icon">⚠️</div>
                <div class="tip-title">Opt-out Button Text (Context-Dependent)</div>
                <div class="tip-body">
                    Buttons like <strong>"Not Interested"</strong> or <strong>"Unsubscribe"</strong> are a
                    <em>risk factor</em>, not a guaranteed rejection. Meta's automated review considers the
                    <strong>combination</strong> of template name, body text, and buttons together.
                    The same button text can be approved in one template and rejected in another.
                    Hard rejections happen for <strong>"Stop"</strong> and <strong>"Opt Out"</strong>
                    since those directly replace WhatsApp's native opt-out mechanism.
                    <div class="tip-fix">✅ Safe choices: "View Details", "Learn More", "Buy Now", "Know More", "Interested"</div>
                </div>
            </div>

            <div class="tip-card">
                <div class="tip-icon">📝</div>
                <div class="tip-title">Placeholder / Test Content</div>
                <div class="tip-body">
                    Body text that looks like a placeholder — <strong>"test description"</strong>,
                    <strong>"sample text"</strong>, <strong>"lorem ipsum"</strong>, <strong>"your message here"</strong>
                    — is flagged immediately by Meta's automated review.
                    <div class="tip-fix">✅ Use real, meaningful marketing copy that a customer would actually receive.</div>
                </div>
            </div>

            <div class="tip-card">
                <div class="tip-icon">⚖️</div>
                <div class="tip-title">Too Few Words for Variables</div>
                <div class="tip-body">
                    If your body text has <strong>{{'{{'}}N{{'}}'}}</strong> variables but too few surrounding words,
                    Meta rejects it. The rule is roughly <strong>3+ real words per variable</strong>.
                    e.g. <code>"Hello {{'{{'}}1{{'}}'}}"</code> is too short.
                    <div class="tip-fix">✅ Use: "Hello {{'{{'}}1{{'}}'}}! Check out our latest exclusive deals and offers."</div>
                </div>
            </div>

            <div class="tip-card">
                <div class="tip-icon">📋</div>
                <div class="tip-title">Duplicate Template Name</div>
                <div class="tip-body">
                    Submitting a template with the same name as an existing one (even a rejected one)
                    causes an error or immediate rejection.
                    <div class="tip-fix">✅ Always use a unique name. Deleted templates can still block the name for 30 days.</div>
                </div>
            </div>

            <div class="tip-card">
                <div class="tip-icon">🔤</div>
                <div class="tip-title">Template Name Format</div>
                <div class="tip-body">
                    Template names must be <strong>lowercase letters, numbers, and underscores only</strong>.
                    No spaces, no hyphens, no special characters. Names are case-sensitive on Meta's side.
                    <div class="tip-fix">✅ Use: <code>summer_sale_2026</code> ❌ Avoid: <code>Summer Sale 2026</code></div>
                </div>
            </div>

            <div class="tip-card">
                <div class="tip-icon">🃏</div>
                <div class="tip-title">Carousel Button Inconsistency</div>
                <div class="tip-body">
                    All carousel cards must have the <strong>same number of buttons</strong> and the
                    <strong>same button type</strong> (all QUICK_REPLY or all URL).
                    Mixing types or having different counts per card causes rejection.
                    <div class="tip-fix">✅ Keep button count and type identical across all cards.</div>
                </div>
            </div>

            <div class="tip-card">
                <div class="tip-icon">📣</div>
                <div class="tip-title">Solicitation / Cold Outreach Language</div>
                <div class="tip-body">
                    Language that implies the recipient did <strong>not opt in</strong> — e.g.
                    <strong>"Are you interested?"</strong>, <strong>"Would you like to know more?"</strong>
                    — signals unsolicited marketing and triggers rejection under MARKETING category.
                    <div class="tip-fix">✅ Write as if the customer already knows your brand and opted in to hear from you.</div>
                </div>
            </div>

            <div class="tip-card">
                <div class="tip-icon">💰</div>
                <div class="tip-title">Prohibited Content Categories</div>
                <div class="tip-body">
                    Meta prohibits templates promoting <strong>alcohol, tobacco, gambling, adult content,
                    pharmaceuticals, weapons, or financial products</strong> (loans, crypto, etc.)
                    in MARKETING category without prior approval.
                    <div class="tip-fix">✅ Keep content to allowed product/service categories.</div>
                </div>
            </div>

            <div class="tip-card">
                <div class="tip-icon">🖼️</div>
                <div class="tip-title">Image Asset Expired</div>
                <div class="tip-body">
                    The asset handles used when submitting a template expire within <strong>~30 minutes</strong>.
                    If you start a template submission and leave it too long before submitting,
                    the handles are stale and Meta rejects the template.
                    <div class="tip-fix">✅ Fill in all details and submit in one sitting without long delays.</div>
                </div>
            </div>

            <div class="tip-card">
                <div class="tip-icon">🌐</div>
                <div class="tip-title">URL Without Registered Domain</div>
                <div class="tip-body">
                    If your template body includes a URL, it must be from a domain that is
                    <strong>registered and verified</strong> in your Meta Business Manager.
                    Unverified or link-shortener URLs are rejected.
                    <div class="tip-fix">✅ Register your domain in Meta Business Settings → Brand Safety → Domains.</div>
                </div>
            </div>

        </div>
    </div>

    <!-- Builder + Preview side by side (toggled) -->
    <div *ngIf="showBuilderForm" class="row g-3 mb-4">

        <!-- Form column -->
        <div class="col-lg-6">
            <div class="card">
                <div class="card-header d-flex align-items-center gap-2">
                    <i class="pe-7s-tools icon-gradient bg-tempting-azure" style="font-size:1.2rem;"></i>
                    <span class="fw-semibold">Template Builder</span>
                </div>
                <div class="card-body" style="max-height: 78vh; overflow-y: auto;">

                    <!-- Format selector -->
                    <div class="mb-3">
                        <label class="form-label fw-semibold">Template Format</label>
                        <div class="d-flex gap-2">
                            <div class="format-option" [class.active]="tplFormat==='text'" (click)="setTplFormat('text')">
                                <div class="format-option__icon">📝</div>
                                <div class="fw-semibold small">Text</div>
                                <div class="text-muted" style="font-size:0.72rem;">Body only</div>
                            </div>
                            <div class="format-option" [class.active]="tplFormat==='image'" (click)="setTplFormat('image')">
                                <div class="format-option__icon">🖼️</div>
                                <div class="fw-semibold small">Image</div>
                                <div class="text-muted" style="font-size:0.72rem;">Header + body</div>
                            </div>
                            <div class="format-option" [class.active]="tplFormat==='carousel'" (click)="setTplFormat('carousel')">
                                <div class="format-option__icon">🎠</div>
                                <div class="fw-semibold small">Carousel</div>
                                <div class="text-muted" style="font-size:0.72rem;">Multiple cards</div>
                            </div>
                        </div>
                    </div>

                    <!-- Meta details -->
                    <div class="row g-2 mb-3">
                        <div class="col-12">
                            <label class="form-label fw-semibold">Template Name</label>
                            <input type="text" class="form-control"
                                [(ngModel)]="tplName"
                                (input)="normalizeTplName()"
                                placeholder="e.g. summer_sale_2026"
                                [class.is-invalid]="!!tplNameError" />
                            <div *ngIf="tplNameError" class="invalid-feedback">{{ tplNameError }}</div>
                            <div class="form-text">Lowercase, numbers, underscores only. Spaces auto-convert to <code>_</code>.</div>
                            <div *ngFor="let w of tplNameWarnings" class="vld-warning mt-1">
                                <i class="pe-7s-attention me-1"></i>{{ w }}
                            </div>
                        </div>
                        <div class="col-md-6">
                            <label class="form-label fw-semibold">Category</label>
                            <select class="form-select" [(ngModel)]="tplCategory">
                                <option value="MARKETING">MARKETING</option>
                                <option value="UTILITY">UTILITY</option>
                            </select>
                        </div>
                        <div class="col-md-6">
                            <label class="form-label fw-semibold">Language</label>
                            <select class="form-select" [(ngModel)]="tplLanguage">
                                <option value="en">English (en)</option>
                                <option value="en_US">English US (en_US)</option>
                                <option value="hi">Hindi (hi)</option>
                            </select>
                        </div>
                    </div>

                    <!-- Template type -->
                    <div class="mb-3">
                        <label class="form-label fw-semibold">Template Type</label>
                        <div class="d-flex gap-2 mt-1">
                            <div class="template-type-option" [class.active]="tplType === 'standard'" (click)="setTplType('standard')">
                                <div class="fw-semibold small">Standard</div>
                                <div class="text-muted" style="font-size:0.75rem;">1 button</div>
                            </div>
                            <div class="template-type-option" [class.active]="tplType === 'engage'" (click)="setTplType('engage')">
                                <div class="fw-semibold small">Engagement</div>
                                <div class="text-muted" style="font-size:0.75rem;">2 buttons</div>
                            </div>
                        </div>
                    </div>

                    <!-- ── TEXT format ─────────────────────────────────────── -->
                    <ng-container *ngIf="tplFormat === 'text'">
                        <div class="mb-3">
                            <label class="form-label fw-semibold">Body Text <span class="text-danger">*</span></label>
                            <div class="body-editor">
                                <div class="body-toolbar">
                                    <button type="button" class="toolbar-btn" title="Bold"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('bold')"><strong>B</strong></button>
                                    <button type="button" class="toolbar-btn toolbar-btn--i" title="Italic"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('italic')"><em>I</em></button>
                                    <button type="button" class="toolbar-btn toolbar-btn--s" title="Strikethrough"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('strikeThrough')"><s>S</s></button>
                                    <span class="toolbar-sep"></span>
                                    <div class="var-btn-group">
                                    <button type="button" class="toolbar-btn toolbar-btn--var" title="Insert next variable at cursor"
                                        (mousedown)="$event.preventDefault()" (click)="insertVarRich(textEditor)">+ Variable</button>
                                    <span class="var-group-sep">|</span>
                                    <details class="var-info-details">
                                        <summary class="toolbar-btn toolbar-btn--info" title="What is a variable?"><i class="pe-7s-info"></i></summary>
                                        <div class="var-info-popover">
                                            <div class="var-info-title">What is a variable?</div>
                                            <div class="var-info-body">A placeholder that gets replaced with real recipient data when the message is sent.</div>
                                            <div class="var-info-example">
                                                <span class="var-info-label">Example</span>
                                                Body: <code>Hello [variable 1], your order is ready!</code><br>
                                                Sent as: <code>Hello Deepak, your order is ready!</code>
                                            </div>
                                        </div>
                                    </details>
                                    </div>
                                </div>
                                <div #textEditor
                                     contenteditable="true"
                                     class="body-richtext"
                                     [class.is-invalid]="bodyErrors(tplSingleBodyText).length > 0"
                                     (focus)="initEditorOnce(textEditor, tplSingleBodyText)"
                                     (input)="onBodyInput($event, 'single')"
                                     aria-label="Body text editor"
                                     role="textbox"
                                     aria-multiline="true"></div>
                            </div>
                            <ng-container *ngFor="let e of bodyErrors(tplSingleBodyText)">
                                <div class="vld-error mt-1"><i class="pe-7s-close-circle me-1"></i>{{ e }}</div>
                            </ng-container>
                            <ng-container *ngFor="let w of bodyWarnings(tplSingleBodyText)">
                                <div class="vld-warning mt-1"><i class="pe-7s-attention me-1"></i>{{ w }}</div>
                            </ng-container>
                        </div>
                        <div class="mb-3">
                            <div class="d-flex align-items-center justify-content-between mb-2">
                                <label class="form-label fw-semibold mb-0">Buttons</label>
                                <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" id="textHasBtns" [(ngModel)]="tplHasButtons" />
                                    <label class="form-check-label small" for="textHasBtns">{{ tplHasButtons ? 'Enabled' : 'No buttons' }}</label>
                                </div>
                            </div>
                            <ng-container *ngIf="tplHasButtons">
                                <div *ngFor="let btn of tplButtonNames; let bi = index; trackBy: trackByIndex" class="mb-1">
                                    <div class="input-group">
                                        <span class="input-group-text" style="min-width:80px;">Button {{ bi + 1 }}</span>
                                        <input type="text" class="form-control" [(ngModel)]="tplButtonNames[bi]" maxlength="20"
                                            [placeholder]="bi === 0 ? 'e.g. View' : 'e.g. Not Interested'"
                                            [class.is-invalid]="!tplButtonNames[bi].trim() || buttonErrors(tplButtonNames[bi]).length > 0" />
                                        <span class="input-group-text text-muted" style="font-size:0.8rem;">{{ tplButtonNames[bi].length }}/20</span>
                                    </div>
                                    <div *ngFor="let e of buttonErrors(tplButtonNames[bi])" class="vld-error mt-1">
                                        <i class="pe-7s-close-circle me-1"></i>{{ e }}
                                    </div>
                                </div>
                            </ng-container>
                        </div>
                    </ng-container>

                    <!-- ── IMAGE format ────────────────────────────────────── -->
                    <ng-container *ngIf="tplFormat === 'image'">
                        <div class="mb-3">
                            <label class="form-label fw-semibold">Header Image <span class="text-danger">*</span></label>
                            <div class="img-drop-zone" (click)="triggerSingleFileInput()" [class.has-image]="!!tplSinglePreview">
                                <img *ngIf="tplSinglePreview" [src]="tplSinglePreview" class="img-preview" style="max-height:140px;" alt="Header" />
                                <div *ngIf="!tplSinglePreview" class="img-drop-hint">
                                    <i class="pe-7s-upload" style="font-size:1.8rem; color:#adb5bd;"></i>
                                    <p class="mt-1 mb-0 text-muted" style="font-size:0.8rem;">Click to upload image</p>
                                </div>
                            </div>
                            <input id="tpl_single_img" type="file" accept="image/*" class="d-none" (change)="onSingleFileSelect($event)" />
                        </div>
                        <div class="mb-3">
                            <label class="form-label fw-semibold">Body Text <span class="text-danger">*</span></label>
                            <div class="body-editor">
                                <div class="body-toolbar">
                                    <button type="button" class="toolbar-btn" title="Bold"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('bold')"><strong>B</strong></button>
                                    <button type="button" class="toolbar-btn toolbar-btn--i" title="Italic"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('italic')"><em>I</em></button>
                                    <button type="button" class="toolbar-btn toolbar-btn--s" title="Strikethrough"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('strikeThrough')"><s>S</s></button>
                                    <span class="toolbar-sep"></span>
                                    <div class="var-btn-group">
                                    <button type="button" class="toolbar-btn toolbar-btn--var" title="Insert next variable at cursor"
                                        (mousedown)="$event.preventDefault()" (click)="insertVarRich(imgEditor)">+ Variable</button>
                                    <span class="var-group-sep">|</span>
                                    <details class="var-info-details">
                                        <summary class="toolbar-btn toolbar-btn--info" title="What is a variable?"><i class="pe-7s-info"></i></summary>
                                        <div class="var-info-popover">
                                            <div class="var-info-title">What is a variable?</div>
                                            <div class="var-info-body">A placeholder that gets replaced with real recipient data when the message is sent.</div>
                                            <div class="var-info-example">
                                                <span class="var-info-label">Example</span>
                                                Body: <code>Hello [variable 1], your order is ready!</code><br>
                                                Sent as: <code>Hello Deepak, your order is ready!</code>
                                            </div>
                                        </div>
                                    </details>
                                    </div>
                                </div>
                                <div #imgEditor
                                     contenteditable="true"
                                     class="body-richtext"
                                     [class.is-invalid]="bodyErrors(tplSingleBodyText).length > 0"
                                     (focus)="initEditorOnce(imgEditor, tplSingleBodyText)"
                                     (input)="onBodyInput($event, 'single')"
                                     aria-label="Body text editor"
                                     role="textbox"
                                     aria-multiline="true"></div>
                            </div>
                            <ng-container *ngFor="let e of bodyErrors(tplSingleBodyText)">
                                <div class="vld-error mt-1"><i class="pe-7s-close-circle me-1"></i>{{ e }}</div>
                            </ng-container>
                            <ng-container *ngFor="let w of bodyWarnings(tplSingleBodyText)">
                                <div class="vld-warning mt-1"><i class="pe-7s-attention me-1"></i>{{ w }}</div>
                            </ng-container>
                        </div>
                        <div class="mb-3">
                            <div class="d-flex align-items-center justify-content-between mb-2">
                                <label class="form-label fw-semibold mb-0">Buttons</label>
                                <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" id="imgHasBtns" [(ngModel)]="tplHasButtons" />
                                    <label class="form-check-label small" for="imgHasBtns">{{ tplHasButtons ? 'Enabled' : 'No buttons' }}</label>
                                </div>
                            </div>
                            <ng-container *ngIf="tplHasButtons">
                                <div *ngFor="let btn of tplButtonNames; let bi = index; trackBy: trackByIndex" class="mb-1">
                                    <div class="input-group">
                                        <span class="input-group-text" style="min-width:80px;">Button {{ bi + 1 }}</span>
                                        <input type="text" class="form-control" [(ngModel)]="tplButtonNames[bi]" maxlength="20"
                                            [placeholder]="bi === 0 ? 'e.g. View' : 'e.g. Not Interested'"
                                            [class.is-invalid]="!tplButtonNames[bi].trim() || buttonErrors(tplButtonNames[bi]).length > 0" />
                                        <span class="input-group-text text-muted" style="font-size:0.8rem;">{{ tplButtonNames[bi].length }}/20</span>
                                    </div>
                                    <div *ngFor="let e of buttonErrors(tplButtonNames[bi])" class="vld-error mt-1">
                                        <i class="pe-7s-close-circle me-1"></i>{{ e }}
                                    </div>
                                </div>
                            </ng-container>
                        </div>
                    </ng-container>

                    <!-- ── CAROUSEL format ─────────────────────────────────── -->
                    <ng-container *ngIf="tplFormat === 'carousel'">
                        <!-- Button scope toggle + global labels -->
                        <div class="mb-3">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <label class="form-label fw-semibold mb-0">Button Labels</label>
                                <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch" id="sameButtonsToggle"
                                        [checked]="tplSameButtonsForAll" (change)="toggleSameButtonsForAll()" />
                                    <label class="form-check-label small" for="sameButtonsToggle">
                                        {{ tplSameButtonsForAll ? 'Same for all cards' : 'Different per card' }}
                                    </label>
                                </div>
                            </div>
                            <ng-container *ngIf="tplSameButtonsForAll">
                                <div *ngFor="let btn of tplButtonNames; let bi = index; trackBy: trackByIndex" class="mb-1">
                                    <div class="input-group">
                                        <span class="input-group-text" style="min-width:80px;">Button {{ bi + 1 }}</span>
                                        <input type="text" class="form-control" [(ngModel)]="tplButtonNames[bi]" maxlength="20"
                                            [placeholder]="bi === 0 ? 'e.g. View' : 'e.g. Not Interested'"
                                            [class.is-invalid]="!tplButtonNames[bi].trim() || buttonErrors(tplButtonNames[bi]).length > 0" />
                                        <span class="input-group-text text-muted" style="font-size:0.8rem;">{{ tplButtonNames[bi].length }}/20</span>
                                    </div>
                                    <div *ngFor="let e of buttonErrors(tplButtonNames[bi])" class="vld-error mt-1">
                                        <i class="pe-7s-close-circle me-1"></i>{{ e }}
                                    </div>
                                </div>
                                <div class="form-text">Applied to every card. Max 20 characters.</div>
                            </ng-container>
                            <div *ngIf="!tplSameButtonsForAll" class="form-text">
                                <i class="pe-7s-info me-1"></i>Button labels set individually per card below.
                            </div>
                        </div>

                        <!-- Outer body -->
                        <div class="mb-3">
                            <label class="form-label fw-semibold">Outer Body Text</label>
                            <div class="body-editor">
                                <div class="body-toolbar">
                                    <button type="button" class="toolbar-btn" title="Bold"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('bold')"><strong>B</strong></button>
                                    <button type="button" class="toolbar-btn toolbar-btn--i" title="Italic"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('italic')"><em>I</em></button>
                                    <button type="button" class="toolbar-btn toolbar-btn--s" title="Strikethrough"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('strikeThrough')"><s>S</s></button>
                                    <span class="toolbar-sep"></span>
                                    <div class="var-btn-group">
                                    <button type="button" class="toolbar-btn toolbar-btn--var" title="Insert next variable at cursor"
                                        (mousedown)="$event.preventDefault()" (click)="insertVarRich(outerEditor)">+ Variable</button>
                                    <span class="var-group-sep">|</span>
                                    <details class="var-info-details">
                                        <summary class="toolbar-btn toolbar-btn--info" title="What is a variable?"><i class="pe-7s-help1"></i></summary>
                                        <div class="var-info-popover">
                                            <div class="var-info-title">What is a variable?</div>
                                            <div class="var-info-body">A placeholder that gets replaced with real recipient data when the message is sent.</div>
                                            <div class="var-info-example">
                                                <span class="var-info-label">Example</span>
                                                Body: <code>Hello [variable 1], your order is ready!</code><br>
                                                Sent as: <code>Hello Deepak, your order is ready!</code>
                                            </div>
                                        </div>
                                    </details>
                                    </div>
                                </div>
                                <div #outerEditor
                                     contenteditable="true"
                                     class="body-richtext"
                                     [class.is-invalid]="bodyErrors(tplOuterBodyText).length > 0"
                                     (focus)="initEditorOnce(outerEditor, tplOuterBodyText)"
                                     (input)="onBodyInput($event, 'outer')"
                                     aria-label="Outer body text editor"
                                     role="textbox"
                                     aria-multiline="true"></div>
                            </div>
                            <ng-container *ngFor="let e of bodyErrors(tplOuterBodyText)">
                                <div class="vld-error mt-1"><i class="pe-7s-close-circle me-1"></i>{{ e }}</div>
                            </ng-container>
                            <ng-container *ngFor="let w of bodyWarnings(tplOuterBodyText)">
                                <div class="vld-warning mt-1"><i class="pe-7s-attention me-1"></i>{{ w }}</div>
                            </ng-container>
                        </div>

                        <hr />

                        <!-- Cards -->
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="fw-semibold">Cards <span class="text-muted fw-normal">({{ tplCards.length }})</span></span>
                            <button class="btn btn-sm btn-outline-primary" (click)="addTplCard()" [disabled]="tplCards.length >= 10">
                                <i class="pe-7s-plus me-1"></i> Add Card
                            </button>
                        </div>
                        <div *ngFor="let card of tplCards; let i = index" class="border rounded p-2 mb-2">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <span class="fw-semibold small text-muted text-uppercase" style="letter-spacing:0.05em;">Card {{ i + 1 }}</span>
                                <button class="btn btn-sm btn-outline-danger btn-remove" (click)="removeTplCard(i)" [disabled]="tplCards.length <= 2">
                                    <i class="pe-7s-trash"></i>
                                </button>
                            </div>
                            <div class="d-flex align-items-center gap-2 mb-2">
                                <button class="btn btn-xs" [class.btn-outline-secondary]="card.useItem" [class.btn-outline-primary]="!card.useItem" style="font-size:0.72rem;padding:2px 8px;" (click)="toggleItemLink(i)">
                                    {{ card.useItem ? '📂 Manual Upload' : '📚 Link Item' }}
                                </button>
                            </div>
                            <ng-container *ngIf="card.useItem">
                                <select class="form-select form-select-sm mb-2" [(ngModel)]="card.itemId" (ngModelChange)="onItemSelect(i, $event)">
                                    <option value="">— Select an item —</option>
                                    <option *ngFor="let item of items" [value]="item._id">{{ item.title }} ({{ item.itemCode }})</option>
                                </select>
                                <div *ngIf="card.itemId" class="item-selected-preview mb-2">
                                    <img *ngIf="card.itemImageUrl" [src]="card.itemImageUrl" class="item-thumb" alt="item" />
                                    <div *ngIf="!card.itemImageUrl" class="item-thumb-placeholder"><i class="pe-7s-photo"></i></div>
                                    <div class="item-thumb-info">
                                        <div class="fw-semibold" style="font-size:0.82rem;">{{ card.itemTitle }}</div>
                                    </div>
                                </div>
                            </ng-container>
                            <ng-container *ngIf="!card.useItem">
                                <div class="img-drop-zone mb-2" (click)="triggerTplFileInput(i)" [class.has-image]="!!card.preview" style="min-height:80px;">
                                    <img *ngIf="card.preview" [src]="card.preview" class="img-preview" style="height:80px;" alt="Sample" />
                                    <div *ngIf="!card.preview" class="img-drop-hint" style="padding:0.6rem;">
                                        <i class="pe-7s-upload" style="font-size:1.4rem; color:#adb5bd;"></i>
                                        <p class="mt-1 mb-0 text-muted" style="font-size:0.75rem;">Upload sample image</p>
                                    </div>
                                </div>
                                <input [id]="'tpl_card_img_' + i" type="file" accept="image/*" class="d-none" (change)="onTplFileSelect($event, i)" />
                            </ng-container>
                            <div class="body-editor mb-1">
                                <div class="body-toolbar">
                                    <button type="button" class="toolbar-btn" title="Bold"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('bold')"><strong>B</strong></button>
                                    <button type="button" class="toolbar-btn toolbar-btn--i" title="Italic"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('italic')"><em>I</em></button>
                                    <button type="button" class="toolbar-btn toolbar-btn--s" title="Strikethrough"
                                        (mousedown)="$event.preventDefault()" (click)="applyFmtRich('strikeThrough')"><s>S</s></button>
                                    <span class="toolbar-sep"></span>
                                    <div class="var-btn-group">
                                    <button type="button" class="toolbar-btn toolbar-btn--var" title="Insert next variable at cursor"
                                        (mousedown)="$event.preventDefault()" (click)="insertVarRich(cardEditor)">+ Variable</button>
                                    <span class="var-group-sep">|</span>
                                    <details class="var-info-details">
                                        <summary class="toolbar-btn toolbar-btn--info" title="What is a variable?"><i class="pe-7s-info"></i></summary>
                                        <div class="var-info-popover">
                                            <div class="var-info-title">What is a variable?</div>
                                            <div class="var-info-body">A placeholder that gets replaced with real recipient data when the message is sent.</div>
                                            <div class="var-info-example">
                                                <span class="var-info-label">Example</span>
                                                Body: <code>Hello [variable 1], your order is ready!</code><br>
                                                Sent as: <code>Hello Deepak, your order is ready!</code>
                                            </div>
                                        </div>
                                    </details>
                                    </div>
                                </div>
                                <div #cardEditor
                                     contenteditable="true"
                                     class="body-richtext"
                                     [class.is-invalid]="bodyErrors(card.bodyText).length > 0"
                                     (focus)="initEditorOnce(cardEditor, card.bodyText)"
                                     (input)="onBodyInput($event, 'card', i)"
                                     [attr.placeholder]="'Card ' + (i+1) + ' body text...'"
                                     aria-label="Card body text editor"
                                     role="textbox"
                                     aria-multiline="true"></div>
                            </div>
                            <ng-container *ngFor="let e of bodyErrors(card.bodyText)">
                                <div class="vld-error mb-1"><i class="pe-7s-close-circle me-1"></i>{{ e }}</div>
                            </ng-container>
                            <ng-container *ngFor="let w of bodyWarnings(card.bodyText)">
                                <div class="vld-warning mb-1"><i class="pe-7s-attention me-1"></i>{{ w }}</div>
                            </ng-container>
                            <ng-container *ngIf="!tplSameButtonsForAll">
                                <div class="mt-2 pt-2 border-top">
                                    <div class="small fw-semibold text-muted mb-1">Buttons for this card</div>
                                    <div *ngFor="let btn of card.buttonNames; let bi = index; trackBy: trackByIndex" class="mb-1">
                                        <div class="input-group">
                                            <span class="input-group-text" style="min-width:80px; font-size:0.8rem;">Btn {{ bi + 1 }}</span>
                                            <input type="text" class="form-control form-control-sm" [(ngModel)]="card.buttonNames[bi]" maxlength="20"
                                                [placeholder]="'Button ' + (bi + 1) + ' text'"
                                                [class.is-invalid]="!card.buttonNames[bi].trim() || buttonErrors(card.buttonNames[bi]).length > 0" />
                                            <span class="input-group-text text-muted" style="font-size:0.75rem;">{{ card.buttonNames[bi].length }}/20</span>
                                        </div>
                                        <div *ngFor="let e of buttonErrors(card.buttonNames[bi])" class="vld-error mt-1">
                                            <i class="pe-7s-close-circle me-1"></i>{{ e }}
                                        </div>
                                    </div>
                                </div>
                            </ng-container>
                        </div>
                    </ng-container>

                    <!-- Validation summary -->
                    <ng-container *ngIf="allFormErrors.length > 0 || allFormWarnings.length > 0">
                        <div class="vld-summary mt-3">
                            <div *ngIf="allFormErrors.length > 0">
                                <div class="vld-summary__heading vld-summary__heading--error">
                                    <i class="pe-7s-close-circle me-1"></i> {{ allFormErrors.length }} issue(s) must be fixed before submitting
                                </div>
                                <ul class="vld-summary__list mb-0">
                                    <li *ngFor="let e of allFormErrors">{{ e }}</li>
                                </ul>
                            </div>
                            <div *ngIf="allFormWarnings.length > 0" [class.mt-2]="allFormErrors.length > 0">
                                <div class="vld-summary__heading vld-summary__heading--warn">
                                    <i class="pe-7s-attention me-1"></i> {{ allFormWarnings.length }} warning(s) — review before submitting
                                </div>
                                <ul class="vld-summary__list mb-0">
                                    <li *ngFor="let w of allFormWarnings">{{ w }}</li>
                                </ul>
                            </div>
                        </div>
                    </ng-container>

                    <!-- Submit -->
                    <div class="mt-3 d-flex justify-content-end">
                        <button class="btn btn-primary px-4" (click)="createTemplate()" [disabled]="!tplIsValid || isCreatingTemplate">
                            <span *ngIf="isCreatingTemplate">
                                <span class="spinner-border spinner-border-sm me-1"></span> Creating...
                            </span>
                            <span *ngIf="!isCreatingTemplate">
                                <i class="pe-7s-cloud-upload me-1"></i> Submit to Meta
                            </span>
                        </button>
                    </div>

                </div>
            </div>
        </div>

        <!-- Preview column -->
        <div class="col-lg-6">
            <div class="card">
                <div class="card-header d-flex align-items-center gap-2">
                    <i class="pe-7s-phone icon-gradient bg-happy-fisher" style="font-size:1.2rem;"></i>
                    <span class="fw-semibold">Live Preview</span>
                    <span class="text-muted fw-normal small">— updates as you type</span>
                </div>
                <div class="card-body d-flex justify-content-center align-items-start"
                    style="background:#e5ddd5; min-height:400px; max-height:78vh; overflow-y:auto;">
                    <div class="wa-mock">
                        <div class="wa-mock-header">
                            <div class="wa-mock-avatar"></div>
                            <span class="wa-mock-name">Vintech Electronic</span>
                        </div>
                        <div class="wa-mock-body">
                            <!-- TEXT preview -->
                            <div *ngIf="tplFormat === 'text'" class="wa-msg-bubble">
                                <div class="wa-msg-text" [innerHTML]="previewHtml(tplSingleBodyText)"></div>
                                <div *ngIf="tplHasButtons" class="wa-card-btns mt-2">
                                    <div *ngFor="let b of tplButtonNames" class="wa-btn">{{ b || '(empty)' }}</div>
                                </div>
                                <div class="wa-msg-time">Now · ✓✓</div>
                            </div>

                            <!-- IMAGE preview -->
                            <div *ngIf="tplFormat === 'image'" class="wa-msg-bubble p-0 overflow-hidden">
                                <div class="wa-card-img-wrap" style="height:140px; border-radius:0;">
                                    <img *ngIf="tplSinglePreview" [src]="tplSinglePreview" class="wa-card-img" />
                                    <div *ngIf="!tplSinglePreview" class="wa-card-img-placeholder">
                                        <i class="pe-7s-photo"></i><span>Header image</span>
                                    </div>
                                </div>
                                <div class="p-2">
                                    <div class="wa-msg-text" [innerHTML]="previewHtml(tplSingleBodyText)"></div>
                                    <div *ngIf="tplHasButtons" class="wa-card-btns mt-2">
                                        <div *ngFor="let b of tplButtonNames" class="wa-btn">{{ b || '(empty)' }}</div>
                                    </div>
                                    <div class="wa-msg-time">Now · ✓✓</div>
                                </div>
                            </div>

                            <!-- CAROUSEL preview -->
                            <div *ngIf="tplFormat === 'carousel'" class="wa-msg-bubble">
                                <div class="wa-msg-text" [innerHTML]="previewHtml(tplOuterBodyText)"></div>
                                <div class="wa-carousel-strip">
                                    <div *ngFor="let card of tplCards; let i = index" class="wa-carousel-card">
                                        <div class="wa-card-img-wrap">
                                            <img *ngIf="card.preview" [src]="card.preview" class="wa-card-img" />
                                            <div *ngIf="!card.preview" class="wa-card-img-placeholder">
                                                <i class="pe-7s-photo"></i>
                                                <span>{{ card.useItem && card.itemTitle ? card.itemTitle : 'Card ' + (i + 1) }}</span>
                                            </div>
                                        </div>
                                        <div class="wa-card-body-text" [innerHTML]="previewHtml(card.bodyText)"></div>
                                        <div class="wa-card-btns">
                                            <div *ngFor="let btnLabel of previewButtonNames(i)" class="wa-btn">
                                                {{ btnLabel || '(empty)' }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="wa-msg-time">Now · ✓✓</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>

    <!-- Created Templates -->
    <div class="card mb-4">
        <div class="card-header d-flex align-items-center gap-2">
            <i class="header-icon pe-7s-ribbon icon-gradient bg-tempting-azure"></i>
            <span class="fw-semibold">Created Templates</span>
            <span class="badge bg-secondary rounded-pill ms-1">{{ templates.length }}</span>
            <span *ngIf="isAutoRefreshing" class="ms-auto d-flex align-items-center gap-2 text-muted" style="font-size:0.8rem;">
                <span class="spinner-border spinner-border-sm" style="width:0.7rem;height:0.7rem;border-width:2px;"></span>
                Auto-checking PENDING status in {{ autoRefreshCountdown }}s
            </span>
        </div>
        <div class="card-body p-0">
            <div *ngIf="isLoadingTemplates" class="text-center p-4 text-muted">
                <span class="spinner-border spinner-border-sm me-2"></span> Loading templates...
            </div>
            <div *ngIf="!isLoadingTemplates && templates.length === 0" class="text-center p-4 text-muted">
                No templates created yet. Click <strong>Create Template</strong> to build your first one.
            </div>
            <vio-table-v2
                *ngIf="!isLoadingTemplates && templates.length > 0"
                [tableData]="templatesForTable"
                [tableConfig]="templatesTableConfig">
            </vio-table-v2>
        </div>
    </div>

</div>
